/**
 * useSeatInventory.js - Hook for seat inventory management
 * Elvis Aloha Collection
 * 
 * Manages:
 * - Fetching seat geometries from canonical endpoint
 * - Ticket assignments
 * - Seat inventory statistics
 * - Row generation workflow
 */

import { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export const useSeatInventory = (chartId, token) => {
  const [seats, setSeats] = useState([]);
  const [template, setTemplate] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch seat geometries
  const fetchSeats = useCallback(async (section = null, row = null) => {
    if (!chartId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_BASE}/api/seat-map/seat-geometries/${chartId}?include_assignments=true`;
      if (section) url += `&section=${section}`;
      if (row) url += `&row=${row}`;
      
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      
      const response = await fetch(url, { headers });
      const data = await response.json();
      
      if (response.ok) {
        setSeats(data.seats || []);
        setTemplate(data.template);
      } else {
        setError(data.detail || 'Failed to fetch seats');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [chartId, token]);

  // Fetch inventory stats
  const fetchStats = useCallback(async (section = null) => {
    if (!chartId) return;
    
    try {
      let url = `${API_BASE}/api/seat-map/inventory-stats/${chartId}`;
      if (section) url += `?section=${section}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (response.ok) {
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [chartId]);

  // Initial fetch
  useEffect(() => {
    fetchSeats();
    fetchStats();
  }, [fetchSeats, fetchStats]);

  // Create seat geometry
  const createSeat = useCallback(async (seatData) => {
    if (!token) {
      setError('Authentication required');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/api/seat-map/seat-geometries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(seatData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await fetchSeats();
        await fetchStats();
        return data.seat;
      } else {
        setError(data.detail || 'Failed to create seat');
        return null;
      }
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [token, fetchSeats, fetchStats]);

  // Assign ticket to seat
  const assignTicket = useCallback(async (assignmentData) => {
    if (!token) {
      setError('Authentication required');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/api/seat-map/assignments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(assignmentData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await fetchSeats();
        await fetchStats();
        return data.assignment;
      } else {
        setError(data.detail || 'Failed to assign ticket');
        return null;
      }
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [token, fetchSeats, fetchStats]);

  // Remove assignment
  const removeAssignment = useCallback(async (assignmentId, reason = null) => {
    if (!token) {
      setError('Authentication required');
      return false;
    }

    try {
      let url = `${API_BASE}/api/seat-map/assignments/${assignmentId}`;
      if (reason) url += `?reason=${encodeURIComponent(reason)}`;
      
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        await fetchSeats();
        await fetchStats();
        return true;
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to remove assignment');
        return false;
      }
    } catch (err) {
      setError(err.message);
      return false;
    }
  }, [token, fetchSeats, fetchStats]);

  // Delete a persisted/saved seat geometry (canonical inventory).
  // Server governs: blocks with 409 if actively assigned or approved/published.
  const deleteSeat = useCallback(async (seatId) => {
    if (!token) return { ok: false, error: 'Authentication required' };

    try {
      const response = await fetch(`${API_BASE}/api/seat-map/seat-geometries/${seatId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchSeats();
        await fetchStats();
        return { ok: true };
      }

      const data = await response.json().catch(() => ({}));
      const detail = data?.detail;
      const message = (detail && typeof detail === 'object') ? detail.message : (detail || 'Failed to delete seat');
      const code = (detail && typeof detail === 'object') ? detail.code : null;
      return { ok: false, error: message, code, status: response.status };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error' };
    }
  }, [token, fetchSeats, fetchStats]);

  // Update a persisted/saved seat geometry's governed fields (e.g. moving
  // geometry_status through draft -> calibrated -> approved -> published).
  // The server auto-syncs owner_approved whenever geometry_status changes.
  const updateSeatGeometry = useCallback(async (seatId, updates) => {
    if (!token) return { ok: false, error: 'Authentication required' };

    try {
      const response = await fetch(`${API_BASE}/api/seat-map/seat-geometries/${seatId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        await fetchSeats();
        await fetchStats();
        return { ok: true };
      }

      const data = await response.json().catch(() => ({}));
      const detail = data?.detail;
      const message = (detail && typeof detail === 'object') ? (detail.message || JSON.stringify(detail)) : (detail || 'Failed to update seat');
      return { ok: false, error: message, status: response.status };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error' };
    }
  }, [token, fetchSeats, fetchStats]);

  // Preview row generation
  const previewRow = useCallback(async (rowData) => {
    if (!token) {
      setError('Authentication required');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/api/seat-map/generate-row-preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(rowData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        return data;
      } else {
        setError(data.detail || 'Failed to preview row');
        return null;
      }
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [token]);

  // Confirm row generation
  const confirmRow = useCallback(async (rowData) => {
    if (!token) {
      setError('Authentication required');
      return null;
    }

    try {
      const response = await fetch(`${API_BASE}/api/seat-map/generate-row-confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(rowData)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        await fetchSeats();
        await fetchStats();
        return data;
      } else {
        setError(data.detail || 'Failed to confirm row');
        return null;
      }
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [token, fetchSeats, fetchStats]);

  // Fetch full owner detail for an assignment (ticket + geometry + mismatch flag)
  const getAssignmentDetail = useCallback(async (assignmentId) => {
    if (!token) return null;
    try {
      const response = await fetch(`${API_BASE}/api/seat-map/assignments/${assignmentId}/detail`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) return data;
      setError(data.detail?.message || data.detail || 'Failed to fetch assignment detail');
      return null;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [token]);

  // Generic helper for the assignment action endpoints below
  const _assignmentAction = useCallback(async (assignmentId, action, method = 'PUT', body = undefined) => {
    if (!token) return { ok: false, error: 'Authentication required' };
    try {
      const response = await fetch(`${API_BASE}/api/seat-map/assignments/${assignmentId}${action}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: body !== undefined ? JSON.stringify(body) : undefined
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        await fetchSeats();
        await fetchStats();
        return { ok: true, data };
      }
      return { ok: false, error: data?.detail || data, status: response.status };
    } catch (err) {
      return { ok: false, error: err.message || 'Network error' };
    }
  }, [token, fetchSeats, fetchStats]);

  // Step 1: Mark Verified (optionally with a documented mismatch exception)
  const verifyAssignment = useCallback((assignmentId, { verificationNote, mismatchException, mismatchReason } = {}) =>
    _assignmentAction(assignmentId, '/verify', 'PUT', {
      verification_note: verificationNote || null,
      mismatch_exception: !!mismatchException,
      mismatch_reason: mismatchReason || null,
    }), [_assignmentAction]);

  // Verified -> Reopened/Pending (auto-unpublishes if it was published)
  const reopenVerification = useCallback((assignmentId, reason) =>
    _assignmentAction(assignmentId, '/reopen', 'PUT', { reason: reason || null }), [_assignmentAction]);

  // Step 2: Publish Assignment (server enforces all prerequisites)
  const publishAssignment = useCallback((assignmentId) =>
    _assignmentAction(assignmentId, '/publish', 'PUT'), [_assignmentAction]);

  // Published -> Unpublished but Verified
  const unpublishAssignment = useCallback((assignmentId, reason) =>
    _assignmentAction(assignmentId, '/unpublish', 'PUT', { reason: reason || null }), [_assignmentAction]);

  // Atomic reassignment to a different ticket
  const reassignTicket = useCallback((assignmentId, newTicketId, reason) =>
    _assignmentAction(assignmentId, '/reassign', 'POST', { new_ticket_id: newTicketId, reason: reason || null }), [_assignmentAction]);

  // Get unassigned tickets for a section
  const getUnassignedTickets = useCallback(async (section) => {
    if (!token) {
      setError('Authentication required');
      return [];
    }

    try {
      const response = await fetch(`${API_BASE}/api/seat-map/unassigned-tickets/${section}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        return data.tickets || [];
      } else {
        setError(data.detail || 'Failed to fetch tickets');
        return [];
      }
    } catch (err) {
      setError(err.message);
      return [];
    }
  }, [token]);

  return {
    seats,
    template,
    stats,
    loading,
    error,
    fetchSeats,
    fetchStats,
    createSeat,
    assignTicket,
    removeAssignment,
    deleteSeat,
    updateSeatGeometry,
    previewRow,
    confirmRow,
    getUnassignedTickets,
    getAssignmentDetail,
    verifyAssignment,
    reopenVerification,
    publishAssignment,
    unpublishAssignment,
    reassignTicket,
    clearError: () => setError(null)
  };
};

export default useSeatInventory;
